import { FormProvider } from 'react-hook-form';

import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import ResolutionCategoryFormContent from 'src/components/resident-care/ResolutionCategoryFormContent';
import { AlertOutcomeTypes } from 'src/constants/AlertOutcomeTypes';
import useResidentCareTaskForm from 'src/hooks/forms/useResidentCareTaskForm';
import useTaskProperties from 'src/hooks/useTaskProperties';
import { Task } from 'src/store/sageAdminApi';

export interface SelectCareTasksAndOutcomesDrawerProps extends DrawerComponentProps {
    residentId: string;
    analyticsPrefix: string;
    task: Task;
}

const SelectCareTasksAndOutcomesDrawer = (props: SelectCareTasksAndOutcomesDrawerProps) => {
    const { residentId, analyticsPrefix, task, onNext } = props;

    const { isEmergency } = useTaskProperties(task);

    const formMethods = useResidentCareTaskForm(residentId, task.id);
    const {
        isLoadingShifts,
        isTestAlert,
        selectedTaskCount,
        reset: resetForm,
        handleSubmit,
        buildFormRequestData,
    } = formMethods;

    const submitOutcomeSelections = () => {
        const formRequestData = buildFormRequestData();

        onNext({
            alertOutcomeType: isTestAlert
                ? AlertOutcomeTypes.TestAlert
                : AlertOutcomeTypes.ResidentInteraction,
            careTaskRequestData: formRequestData.careTaskData,
            careOutcomes: formRequestData.careOutcomes,
            isEmergency,
            analyticsPrefix,
            task,
        });
    };

    return (
        <FormProvider {...formMethods}>
            <DrawerTitle sx={{ p: 0, gap: 2 }}>
                <Box sx={{ px: 2.5, pt: 9 }}>
                    <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                        Select all care provided
                    </Typography>
                </Box>
                <Divider />
            </DrawerTitle>
            <DrawerBody allowShrink>
                <ResolutionCategoryFormContent residentId={residentId} task={task} />
            </DrawerBody>
            <DrawerFooter showShadow sx={{ bgcolor: 'common.white' }}>
                <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center' }}>
                    <Button
                        variant="text"
                        color="primary"
                        onClick={() => resetForm()}
                        disableFocusRipple
                        disableElevation
                        disabled={selectedTaskCount === 0}
                    >
                        <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
                            Clear All
                        </Typography>
                    </Button>

                    <Box
                        component="form"
                        onSubmit={handleSubmit(submitOutcomeSelections)}
                        sx={{ flex: 1 }}
                    >
                        <Button
                            fullWidth
                            color="primary"
                            type="submit"
                            variant="contained"
                            disableFocusRipple
                            disableElevation
                            disabled={selectedTaskCount === 0 || isLoadingShifts}
                        >
                            {`Select${selectedTaskCount > 0 ? ` (${selectedTaskCount})` : ''}`}
                        </Button>
                    </Box>
                </Stack>
            </DrawerFooter>
        </FormProvider>
    );
};

export default SelectCareTasksAndOutcomesDrawer;
